// Refactor 02/2026 — extrahiert aus AppointmentsPage.js (Phase 4, Iter 332).
// Die komplette Tages-Ansicht des Kalenders:
//   - Trainer-Spalten-Header (mit DnD-Reorder)
//   - Zeit-Spalte links
//   - Pro (timeSlot × trainer) eine drop-zone-Zelle, die:
//       • Right-Click → Availability-Dialog
//       • Click → Buchung oder Move-To-Slot
//       • DnD → handleDragOver/Leave/Drop
//       • beim ersten Slot: alle Termine als AppointmentBlock + Mindbody-
//         Overlay + DropPreview
//   - Strichlinie „heute" über CurrentTimeIndicator (in der Page selbst)

import React from 'react';
import { Plus, Lock, Pause } from 'lucide-react';
import { TrainerColumnHeader } from './TrainerColumnHeader';
import { MindbodyOverlay } from './MindbodyOverlay';
import { DropPreview } from './DropPreview';
import { AppointmentBlock } from './AppointmentBlock';
import { toLocalYMD } from './calendarUtils';
import { longPressHandlers } from './touchLongPress';

// 06/2026: Manuelle Sperren (trainer_blocks / studio_blocks) als sichtbarer,
// farbiger Block im Kalender berechnen (absolut positioniert, wie Termine).
const computeBlockOverlays = ({
  trainerData, trainerBlocks, studioBlocks, dateStr, studioFilter,
  calendarStartMinutes, slotHeight, totalRows,
}) => {
  const trainerId = trainerData.trainer?.id || trainerData.id;
  const tStudios = [
    ...((trainerData.trainer?.studio_ids) || trainerData.studio_ids || []),
    trainerData.trainer?.studio_id || trainerData.studio_id,
  ].filter(Boolean);
  const fullHeight = totalRows * (slotHeight / 2);

  const toSpan = (b, type) => {
    if (b.all_day) return { top: 0, height: fullHeight, allDay: true };
    const [sH, sM] = (b.start_time || '00:00').split(':').map(Number);
    const [eH, eM] = (b.end_time || '23:59').split(':').map(Number);
    const startMin = sH * 60 + sM;
    const endMin = eH * 60 + eM;
    let top = ((startMin - calendarStartMinutes) / 30) * slotHeight;
    let height = ((endMin - startMin) / 30) * slotHeight;
    if (top < 0) { height += top; top = 0; }
    if (height <= 0) return null;
    return { top, height, allDay: false };
  };

  const out = [];
  (trainerBlocks || []).forEach((b) => {
    if (b.trainer_id !== trainerId || b.date !== dateStr) return;
    const span = toSpan(b, 'trainer');
    if (span) out.push({ block: b, type: 'trainer', ...span });
  });
  (studioBlocks || []).forEach((b) => {
    if (b.date !== dateStr) return;
    const applies =
      (studioFilter && studioFilter !== 'all' && b.studio_id === studioFilter) ||
      tStudios.includes(b.studio_id);
    if (!applies) return;
    const span = toSpan(b, 'studio');
    if (span) out.push({ block: b, type: 'studio', ...span });
  });
  return out;
};

// Iter 09/2026 (Daniela versteckt): Spalten-Layout für überlappende Termine.
// Zwei zeitgleiche (parallele) Termine desselben Trainers wurden bisher exakt
// übereinander gerendert → einer verschwand hinter dem anderen. Wir weisen
// jeder überlappenden Gruppe Spalten zu (wie Google Kalender), sodass sie
// nebeneinander erscheinen. Abgesagte Termine sind ausgenommen (sie werden
// weiterhin als grauer Streifen dargestellt).
const computeAppointmentColumns = (appts) => {
  const result = {};
  const events = (appts || [])
    .filter(
      (a) =>
        a &&
        a.id &&
        a.status !== 'cancelled' &&
        a.status !== 'no_show' &&
        a.start_time &&
        a.end_time
    )
    .map((a) => ({
      id: a.id,
      start: new Date(a.start_time).getTime(),
      end: new Date(a.end_time).getTime(),
      col: 0,
    }))
    .sort((a, b) => a.start - b.start || a.end - b.end);

  let cluster = [];
  let clusterEnd = -Infinity;

  const flush = () => {
    if (cluster.length === 0) return;
    const columnEnds = [];
    cluster.forEach((ev) => {
      let placed = false;
      for (let c = 0; c < columnEnds.length; c++) {
        if (columnEnds[c] <= ev.start) {
          ev.col = c;
          columnEnds[c] = ev.end;
          placed = true;
          break;
        }
      }
      if (!placed) {
        ev.col = columnEnds.length;
        columnEnds.push(ev.end);
      }
    });
    const total = columnEnds.length;
    cluster.forEach((ev) => {
      result[ev.id] = { colIndex: ev.col, colCount: total };
    });
    cluster = [];
    clusterEnd = -Infinity;
  };

  events.forEach((ev) => {
    if (cluster.length > 0 && ev.start >= clusterEnd) flush();
    cluster.push(ev);
    clusterEnd = Math.max(clusterEnd, ev.end);
  });
  flush();
  return result;
};

export const DayViewGrid = ({
  // Trainer & Layout
  gridRef,
  orderedFilteredTrainers,
  timeSlots,
  slotHeight,
  calendarStartMinutes,
  currentDate,
  // Filter / Movement
  studioFilter,
  movingAppointment,
  onMoveIntoAppointment,
  // Daten
  appointmentsByTrainerForDate,
  serviceMap,
  services,
  customers,
  mindbodySlotsByTrainer,
  anamneseStatus,
  minServiceTotalDuration,
  // Helpers
  isTrainerAvailable,
  isSlotOpenedByException,
  getAdminOnlyInfo,
  isTrainerOnVacation,
  getMaxGapMinutes,
  getAppointmentColor,
  findMindbodyMappedSlotAt,
  // Trainer-Header DnD
  draggingTrainerId,
  setDraggingTrainerId,
  dragOverTrainerId,
  setDragOverTrainerId,
  handleTrainerColumnDrop,
  // Slot-Klick-Handler
  handleSlotClick,
  handleAvailableSlotClick,
  handleUnavailableSlotClick,
  handleMoveToSlot,
  handleRightClick,
  // DragDrop
  draggedAppointment,
  draggedParticipant,
  setDraggedParticipant,
  dropTarget,
  setDropTarget,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleParticipantDragStart,
  handleDragEnd,
  // Booking-Dialog Setter
  setSelectedSlot,
  setSelectedTrainer,
  setBookingData,
  setCustomTime,
  setIsAddingParticipant,
  setAvailableServices,
  setBookingDialogOpen,
  // Appointments-Setter (für optimistic Mindbody push retry)
  setAppointments,
  // Fetcher
  fetchAppointments,
  fetchServices,
  fetchCustomers,
  fetchAvailableServices,
  // Density-Aware Config (07/2026)
  densityConfig,
  appointments = [],
  getSlotBlockReason,
  // 06/2026: Manuelle Sperren sichtbar machen + editierbar
  trainerBlocks = [],
  studioBlocks = [],
  onBlockClick,
}) => (
  <table className="w-full border-collapse" style={{ borderSpacing: 0 }}>
    <thead>
      <tr
        className="bg-gray-50"
        style={{ boxShadow: 'inset 0 -1px 0 #e5e5e5' }}
      >
        <th className="p-3 text-left font-semibold text-sm sticky left-0 bg-gray-50 z-10 w-24">
          Zeit
        </th>
        {orderedFilteredTrainers.map((trainerData) => (
          <TrainerColumnHeader
            key={trainerData.trainer.id}
            trainerData={trainerData}
            studioFilter={studioFilter}
            draggingTrainerId={draggingTrainerId}
            dragOverTrainerId={dragOverTrainerId}
            setDraggingTrainerId={setDraggingTrainerId}
            setDragOverTrainerId={setDragOverTrainerId}
            onColumnDrop={handleTrainerColumnDrop}
            isTrainerOnVacation={isTrainerOnVacation}
          />
        ))}
      </tr>
    </thead>
    <tbody ref={gridRef}>
      {timeSlots.map((timeSlot, slotIndex) => (
        <tr key={timeSlot} className="hover:bg-gray-50">
          <td
            className="text-sm font-medium text-gray-600 sticky left-0 bg-white border-r"
            style={{
              height: `${slotHeight / 2}px`,
              padding: '0 8px',
              verticalAlign: 'middle',
              boxShadow: 'inset 0 -1px 0 #e5e5e5',
            }}
          >
            {/* Labels nur an :00 und :30 — 15-Min-Zwischen-Rows bleiben optisch leer */}
            {timeSlot.endsWith(':00') || timeSlot.endsWith(':30')
              ? timeSlot
              : ''}
          </td>
          {orderedFilteredTrainers.map((trainerData) => {
            const isAvailable = isTrainerAvailable(trainerData, timeSlot);
            const onVacationDay = isTrainerOnVacation
              ? isTrainerOnVacation(trainerData.trainer.id)
              : null;
            const exceptionOpen =
              isAvailable && isSlotOpenedByException
                ? isSlotOpenedByException(trainerData, timeSlot)
                : null;
            const adminOnlyInfo =
              isAvailable && getAdminOnlyInfo
                ? getAdminOnlyInfo(trainerData, timeSlot)
                : null;
            const adminOnlyLabel = adminOnlyInfo
              ? adminOnlyInfo.reason === 'expired'
                ? `⏳ Abgelaufen${adminOnlyInfo.validUntil ? ` (Kundenfenster bis ${adminOnlyInfo.validUntil})` : ''} — nur Admin/Trainer buchbar`
                : adminOnlyInfo.reason === 'not_yet'
                ? `🗓️ Für Kunden erst ab ${adminOnlyInfo.validFrom} — aktuell nur Admin/Trainer buchbar`
                : '🔒 Für Kunden ausgeblendet — nur Admin/Trainer buchbar'
              : null;
            const isFirstSlot = slotIndex === 0;
            // 10/2026 Drop-Präzision: exakten 15-Min-Slot unter dem Cursor
            // hervorheben (dropTarget.minutes = absolute Minuten, auf 15 gerundet).
            const _cellParts = timeSlot.split(':').map(Number);
            const _cellMinutes = _cellParts[0] * 60 + _cellParts[1];
            const isDropSlot =
              dropTarget?.trainer?.trainer?.id === trainerData.trainer.id &&
              dropTarget?.minutes === _cellMinutes;
            const isBlockedDropSlot = isDropSlot && !!dropTarget?.blocked;

            return (
              <td
                key={`${trainerData.trainer.id}-${timeSlot}`}
                data-drop-active={isDropSlot ? 'true' : undefined}
                className={`drop-zone p-0 border-l cursor-pointer transition-colors ${
                  onVacationDay && !isAvailable
                    ? 'vacation-hatch'
                    : isAvailable
                    ? movingAppointment
                      ? 'bg-green-50 hover:bg-green-100'
                      : 'bg-white hover:bg-teal-50'
                    : 'bg-gray-100'
                } ${
                  isBlockedDropSlot
                    ? 'bg-red-200 ring-2 ring-inset ring-red-500'
                    : isDropSlot
                    ? 'bg-teal-200 ring-2 ring-inset ring-teal-500'
                    : ''
                }`}
                onDragOver={(e) => handleDragOver(e, trainerData, timeSlot)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, trainerData, timeSlot)}
                title={
                  onVacationDay && !isAvailable
                    ? `🏖️ In Ferien (${new Date(onVacationDay.start_date).toLocaleDateString('de-CH')} – ${new Date(onVacationDay.end_date).toLocaleDateString('de-CH')}) — für Kunden gesperrt`
                    : adminOnlyLabel
                    ? adminOnlyLabel
                    : exceptionOpen
                    ? `Per Ausnahme geöffnet (Blocker ${exceptionOpen.block.start_time}–${exceptionOpen.block.end_time} Uhr)`
                    : undefined
                }
                data-exception-open={exceptionOpen ? 'true' : undefined}
                data-admin-only={adminOnlyInfo ? adminOnlyInfo.reason : undefined}
                onContextMenu={(e) => {
                  e.preventDefault();
                  if (isAvailable) {
                    handleAvailableSlotClick(trainerData, timeSlot, exceptionOpen);
                  } else {
                    handleUnavailableSlotClick(trainerData, timeSlot);
                  }
                }}
                // Iter 351n: Long-Press = Rechtsklick-Ersatz auf Touch-Geräten
                {...longPressHandlers(() => {
                  if (isAvailable) {
                    handleAvailableSlotClick(trainerData, timeSlot, exceptionOpen);
                  } else {
                    handleUnavailableSlotClick(trainerData, timeSlot);
                  }
                })}
                style={{
                  height: `${slotHeight / 2}px`,
                  verticalAlign: 'top',
                  position: 'relative',
                  boxShadow: 'inset 0 -1px 0 #f0f0f0',
                  ...(adminOnlyInfo
                    ? {
                        backgroundImage:
                          'repeating-linear-gradient(135deg, rgba(245,158,11,0.20) 0px, rgba(245,158,11,0.20) 5px, transparent 5px, transparent 11px)',
                      }
                    : exceptionOpen
                    ? {
                        backgroundImage:
                          'repeating-linear-gradient(135deg, rgba(217,119,6,0.10) 0px, rgba(217,119,6,0.10) 4px, transparent 4px, transparent 10px)',
                      }
                    : {}),
                }}
                onClick={() => {
                  if (isAvailable) {
                    if (movingAppointment) {
                      handleMoveToSlot(trainerData, timeSlot);
                    } else {
                      handleSlotClick(trainerData, timeSlot);
                    }
                  } else {
                    // Iter 324: Falls der „unavailable" Slot mit einem
                    // gemappten Mindbody-BELEGT-Slot überlappt, direkt
                    // den Buchungsdialog mit Auto-Select öffnen.
                    const _mbHit = findMindbodyMappedSlotAt(
                      trainerData.trainer.id,
                      timeSlot
                    );
                    if (_mbHit) {
                      handleSlotClick(trainerData, timeSlot);
                    } else {
                      handleUnavailableSlotClick(trainerData, timeSlot);
                    }
                  }
                }}
              >
                {/* Termine + Quick-Add nur im ersten Slot rendern */}
                {isFirstSlot &&
                  (() => {
                    const _trainerAppts =
                      appointmentsByTrainerForDate[trainerData.trainer.id] || [];
                    const _colMap = computeAppointmentColumns(_trainerAppts);
                    return _trainerAppts.map((apt) => (
                      <AppointmentBlock
                        key={apt.id}
                        apt={apt}
                        colIndex={_colMap[apt.id]?.colIndex ?? 0}
                        colCount={_colMap[apt.id]?.colCount ?? 1}
                        trainerData={trainerData}
                        trainerAppointments={
                          appointmentsByTrainerForDate[trainerData.trainer.id] || []
                        }
                        slotHeight={slotHeight}
                        calendarStartMinutes={calendarStartMinutes}
                        currentDate={currentDate}
                        serviceMap={serviceMap}
                        services={services}
                        customers={customers}
                        mindbodySlotsByTrainer={mindbodySlotsByTrainer}
                        movingAppointment={movingAppointment}
                        onMoveIntoAppointment={onMoveIntoAppointment}
                        anamneseStatus={anamneseStatus}
                        minServiceTotalDuration={minServiceTotalDuration}
                        isTrainerOnVacation={isTrainerOnVacation}
                        getMaxGapMinutes={getMaxGapMinutes}
                        getAppointmentColor={getAppointmentColor}
                        findMindbodyMappedSlotAt={findMindbodyMappedSlotAt}
                        draggedParticipant={draggedParticipant}
                        setDraggedParticipant={setDraggedParticipant}
                        setDropTarget={setDropTarget}
                        handleDragOver={handleDragOver}
                        handleDragLeave={handleDragLeave}
                        handleDrop={handleDrop}
                        handleParticipantDragStart={handleParticipantDragStart}
                        handleDragEnd={handleDragEnd}
                        handleRightClick={handleRightClick}
                        setSelectedSlot={setSelectedSlot}
                        setSelectedTrainer={setSelectedTrainer}
                        setBookingData={setBookingData}
                        setCustomTime={setCustomTime}
                        setIsAddingParticipant={setIsAddingParticipant}
                        setAvailableServices={setAvailableServices}
                        setBookingDialogOpen={setBookingDialogOpen}
                        setAppointments={setAppointments}
                        fetchAppointments={fetchAppointments}
                        fetchCustomers={fetchCustomers}
                        fetchServices={fetchServices}
                        fetchAvailableServices={fetchAvailableServices}
                        densityConfig={densityConfig}
                        appointments={appointments}
                        getSlotBlockReason={getSlotBlockReason}
                      />
                    ));
                  })()}

                {/* 06/2026: Manuelle Sperren als farbiger Block mit Grund-Text */}
                {isFirstSlot &&
                  computeBlockOverlays({
                    trainerData,
                    trainerBlocks,
                    studioBlocks,
                    dateStr: toLocalYMD(currentDate),
                    studioFilter,
                    calendarStartMinutes,
                    slotHeight,
                    totalRows: timeSlots.length,
                  }).map((ov, i) => {
                    const isStudio = ov.type === 'studio';
                    const reason = (ov.block.reason || '').trim();
                    const timeLabel = ov.allDay
                      ? 'Ganztägig'
                      : `${ov.block.start_time}–${ov.block.end_time}`;
                    return (
                      <div
                        key={`block-${ov.type}-${ov.block.id || i}`}
                        data-testid={`calendar-block-${ov.type}-${ov.block.id || i}`}
                        title={`${isStudio ? '🏢 Studio-Sperre' : '⏸️ Gesperrt'} · ${timeLabel}${reason ? ` · ${reason}` : ''} — klicken zum Bearbeiten`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onBlockClick) onBlockClick(ov.block, ov.type);
                        }}
                        style={{
                          position: 'absolute',
                          top: `${ov.top}px`,
                          left: '2px',
                          right: '2px',
                          height: `${ov.height}px`,
                          zIndex: 6,
                          borderRadius: '6px',
                          border: `1.5px solid ${isStudio ? '#b45309' : '#be123c'}`,
                          background: isStudio ? 'rgba(217,119,6,0.90)' : 'rgba(225,29,72,0.88)',
                          backgroundImage:
                            'repeating-linear-gradient(135deg, rgba(255,255,255,0.18) 0px, rgba(255,255,255,0.18) 6px, transparent 6px, transparent 13px)',
                          color: '#fff',
                          cursor: 'pointer',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '2px 6px',
                          textAlign: 'center',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        }}
                      >
                        <div className="flex items-center gap-1 font-semibold" style={{ fontSize: '12px', lineHeight: 1.2 }}>
                          {isStudio ? <Lock size={12} /> : <Pause size={12} />}
                          <span>{isStudio ? 'Studio gesperrt' : 'Gesperrt'}</span>
                        </div>
                        {reason && (
                          <div style={{ fontSize: '11px', lineHeight: 1.25, marginTop: '2px', opacity: 0.95, wordBreak: 'break-word' }}>
                            {reason}
                          </div>
                        )}
                        <div style={{ fontSize: '10px', opacity: 0.85, marginTop: '1px' }}>{timeLabel}</div>
                      </div>
                    );
                  })}

                {/* Mindbody BELEGT-Overlay (gruppiert/kapazitäts-aware) */}
                {isFirstSlot && (
                  <MindbodyOverlay
                    trainerId={trainerData.trainer.id}
                    mindbodySlotsByTrainer={mindbodySlotsByTrainer}
                    calendarStartMinutes={calendarStartMinutes}
                    slotHeight={slotHeight}
                  />
                )}

                {/* Drop Preview – zeigt wo Termin/Teilnehmer landet */}
                <DropPreview
                  draggedAppointment={draggedAppointment}
                  draggedParticipant={draggedParticipant}
                  dropTarget={dropTarget}
                  trainerData={trainerData}
                  timeSlot={timeSlot}
                  slotHeight={slotHeight}
                />

                {isAvailable && (
                  <div
                    style={{ height: `${slotHeight / 2}px` }}
                    className={`flex items-center justify-center cursor-pointer ${
                      adminOnlyInfo
                        ? 'text-amber-600 hover:text-amber-700'
                        : 'text-gray-400 hover:text-teal-600'
                    }`}
                    data-testid={adminOnlyInfo ? `admin-only-slot-${trainerData.trainer.id}-${timeSlot}` : undefined}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (movingAppointment) {
                        handleMoveToSlot(trainerData, timeSlot);
                      } else {
                        handleSlotClick(trainerData, timeSlot);
                      }
                    }}
                  >
                    {adminOnlyInfo ? <Lock size={13} /> : <Plus size={14} />}
                  </div>
                )}
              </td>
            );
          })}
        </tr>
      ))}
    </tbody>
  </table>
);

export default DayViewGrid;
